import { BlindKingsGame } from '@blind-kings/game-core';
import { useEffect, useState } from 'react';
import type { RawPiece } from '@blind-kings/game-core';
import previewGame from '../data/preview-game.json';

/** Decode preview frames once at module load — avoids re-decoding every step. */
const PREVIEW_BOARDS: RawPiece[][][] = (() => {
  const { history = [] } = previewGame as { history?: string[] };
  return history.map((encoded) => {
    const state = BlindKingsGame.decodeGameState(encoded);
    return new BlindKingsGame(state).board ?? [];
  });
})();

export default function useHome(paused = false) {
  const [stateIndex, setStateIndex] = useState(0);
  const [pageHidden, setPageHidden] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );
  const [reduceMotion, setReduceMotion] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduceMotion(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onVis = () => setPageHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const freeze = paused || pageHidden || reduceMotion;

  useEffect(() => {
    if (freeze || PREVIEW_BOARDS.length <= 1) return undefined;

    const id = setInterval(() => {
      setStateIndex((i) => (i + 1) % PREVIEW_BOARDS.length);
    }, 900);
    return () => clearInterval(id);
  }, [freeze]);

  return { board: PREVIEW_BOARDS[stateIndex] ?? PREVIEW_BOARDS[0] ?? [] };
}
